import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, Image as ImageIcon, X, AlertCircle } from 'lucide-react'
import { cn, formatBytes } from '@/lib/utils'
import { Button } from '@/components/ui'
import { trackEvent } from '@/lib/gtag'

interface DropzoneProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  maxSize?: number
  accept?: Record<string, string[]>
  className?: string
}

const DEFAULT_ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic', '.heif'],
}

const DEFAULT_MAX_SIZE = 20 * 1024 * 1024 // 20MB

export function Dropzone({
  onFileSelect,
  isLoading = false,
  maxSize = DEFAULT_MAX_SIZE,
  accept = DEFAULT_ACCEPT,
  className,
}: DropzoneProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: { errors: readonly { message: string }[] }[]) => {
      setError(null)

      if (rejectedFiles.length > 0) {
        const err = rejectedFiles[0].errors[0]
        if (err.message.includes('larger')) {
          setError(`File too large. Maximum ${formatBytes(maxSize)}`)
        } else {
          setError('Unsupported file format')
        }
        return
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        setSelectedFile(file)

        // Create preview
        const reader = new FileReader()
        reader.onload = () => {
          setPreview(reader.result as string)
        }
        reader.readAsDataURL(file)
      }
    },
    [maxSize]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled: isLoading,
  })

  const handleClear = () => {
    setPreview(null)
    setSelectedFile(null)
    setError(null)
  }

  const handleSubmit = () => {
    if (selectedFile) {
      trackEvent('file_upload', {
        file_type: selectedFile.type,
        file_size: selectedFile.size,
      })
      onFileSelect(selectedFile)
    }
  }

  // Show preview state
  if (preview && selectedFile) {
    return (
      <div className={cn('w-full', className)}>
        <div className="relative rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-64 object-contain bg-white"
          />
          <button
            onClick={handleClear}
            className="absolute top-3 right-3 p-2 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"
            disabled={isLoading}
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ImageIcon className="w-4 h-4" />
            <span className="truncate max-w-[200px]">{selectedFile.name}</span>
            <span className="text-gray-400">({formatBytes(selectedFile.size)})</span>
          </div>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            {isLoading ? 'Processing...' : 'Remove Watermark'}
          </Button>
        </div>
      </div>
    )
  }

  // Show dropzone
  return (
    <div className={cn('w-full', className)}>
      <div
        {...getRootProps()}
        className={cn(
          'flex flex-col items-center justify-center gap-4 min-h-[300px] p-8',
          'border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200',
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 bg-white hover:border-primary-400 hover:bg-primary-50/50',
          error && 'border-red-400 bg-red-50',
          isLoading && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />

        <div
          className={cn(
            'p-4 rounded-full',
            isDragActive ? 'bg-primary-100' : 'bg-gray-100'
          )}
        >
          <Upload
            className={cn(
              'w-8 h-8',
              isDragActive ? 'text-primary-600' : 'text-gray-400'
            )}
          />
        </div>

        <div className="text-center">
          <p
            className={cn(
              'text-lg font-medium',
              isDragActive ? 'text-primary-700' : 'text-gray-700'
            )}
          >
            {isDragActive
              ? 'Drop image here'
              : 'Drag & drop your image here'}
          </p>
          <p className="mt-1 text-gray-500">or click to browse</p>
        </div>

        <p className="text-sm text-gray-400">
          PNG, JPG, WebP до {formatBytes(maxSize)}
        </p>
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  )
}
